import React from 'react';
import { motion } from 'framer-motion';
import { Star, CheckCircle } from 'lucide-react';
const testimonials = [{
  title: 'From 3 foods to 12 in just 4 weeks',
  text: "I was terrified Leo would end up with scurvy. He only ate one brand of crackers. After 14 days of the Sensory Victory method, he touched a strawberry. By day 30, he ate a grilled chicken strip. This saved our family's sanity.",
  author: 'Sarah M.',
  location: 'Austin, TX',
  date: '2 weeks ago',
  rating: 5,
  verified: true
}, {
  title: 'He actually asked for an apple',
  text: "We were stuck on 4 safe foods for two years. The 'Micro-Shifts' concept completely changed how we approach dinner. He actually asked for an apple yesterday.",
  author: 'Jessica T.',
  location: 'Chicago, IL',
  date: '1 month ago',
  rating: 5,
  verified: true
}, {
  title: 'Dinner is finally peaceful',
  text: 'I was so tired of the power struggles and crying at the table. This method took the pressure off both of us. Dinner is actually peaceful now.',
  author: 'Michael R.',
  location: 'Denver, CO',
  date: '3 weeks ago',
  rating: 5,
  verified: true
}, {
  title: 'Texture sensitivity is improving',
  text: 'My daughter used to gag at anything mushy. The step-by-step texture hacking guide gave us a clear path forward. We are finally making progress without the tears.',
  author: 'Emily L.',
  location: 'Portland, OR',
  date: '1 week ago',
  rating: 5,
  verified: true
}, {
  title: 'Mealtime anxiety is gone',
  text: 'The anxiety around dinner time was destroying our evenings. Implementing these strategies brought calm back to our house. Highly recommend for any parent struggling.',
  author: 'David K.',
  location: 'Miami, FL',
  date: '2 months ago',
  rating: 5,
  verified: true
}];
function StarRating({
  count = 5,
  size = 'sm'
}: {
  count?: number;
  size?: 'sm' | 'lg';
}) {
  const iconSize = size === 'lg' ? 'w-8 h-8 md:w-10 md:h-10' : 'w-4 h-4 md:w-5 md:h-5';
  const gap = size === 'lg' ? 'gap-1' : 'gap-0.5';
  return <div className={`flex items-center ${gap}`} data-id="element-307">
      {Array.from({
      length: 5
    }).map((_, i) => <div key={i} className={`${iconSize} bg-[var(--accent)] flex items-center justify-center ${size === 'lg' ? 'rounded-md' : 'rounded-sm'}`} data-id="element-308">
          <Star className={`${size === 'lg' ? 'w-5 h-5 md:w-6 md:h-6' : 'w-3 h-3 md:w-3.5 md:h-3.5'} fill-white text-white`} data-id="element-309" />
        </div>)}
    </div>;
}
export function TestimonialsSection() {
  return <section className="py-12 md:py-20 px-4 md:px-6 bg-[var(--bg-deep)]" data-id="element-310">
      <div className="max-w-[1200px] mx-auto" data-id="element-311">
        {/* Trustpilot-style Header Summary */}
        <div className="flex flex-col items-center justify-center mb-12 md:mb-16 text-center" data-id="element-312">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-heading)] mb-4" data-id="element-313">
            Excellent
          </h2>
          <div className="mb-4" data-id="element-314">
            <StarRating size="lg" data-id="element-315" />
          </div>
          <p className="text-base md:text-lg text-[var(--text-primary)]" data-id="element-316">
            Based on{' '}
            <span className="font-bold underline decoration-[var(--border-subtle)] underline-offset-4" data-id="element-317">
              2,400+ reviews
            </span>
          </p>
          <div className="mt-4 flex items-center gap-2 text-[var(--text-muted)] text-sm font-medium" data-id="element-318">
            <Star className="w-5 h-5 fill-[var(--accent)] text-[var(--accent)]" data-id="element-319" />
            <span data-id="element-320">Real Parent Results</span>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-321">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-[var(--bg-card)] rounded-lg border border-[var(--border-subtle)] p-6 flex flex-col gap-4" data-id="element-322">
              {/* Stars & Date */}
              <div className="flex items-center justify-between" data-id="element-323">
                <StarRating count={t.rating} data-id="element-324" />
                <span className="text-[var(--text-muted)] text-xs md:text-sm" data-id="element-325">
                  {t.date}
                </span>
              </div>

              {/* Title & Body */}
              <div className="flex-1" data-id="element-326">
                <h3 className="text-[var(--text-heading)] font-bold text-base md:text-lg mb-2 leading-snug" data-id="element-327">
                  {t.title}
                </h3>
                <p className="text-[var(--text-primary)] text-sm md:text-base leading-relaxed" data-id="element-328">
                  {t.text}
                </p>
              </div>

              {/* Author & Verified */}
              <div className="pt-4 border-t border-[var(--border-subtle)]/50 mt-2" data-id="element-329">
                <p className="text-[var(--text-heading)] font-bold text-sm" data-id="element-330">
                  {t.author}
                </p>
                <div className="flex items-center justify-between mt-1" data-id="element-331">
                  <p className="text-[var(--text-muted)] text-xs" data-id="element-332">
                    {t.location}
                  </p>
                  {t.verified && <div className="flex items-center gap-1 text-[var(--text-muted)]" data-id="element-333">
                      <CheckCircle className="w-3.5 h-3.5 text-[var(--text-muted)]" data-id="element-334" />
                      <span className="text-xs font-medium" data-id="element-335">Verified</span>
                    </div>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}